/**
 * Formats Firebase error codes into user-friendly messages
 * @param {Error|Object} error The error object from Firebase
 * @returns {string} User-friendly error message
 */
export const formatFirebaseError = (error) => {
    const code = error?.code || error?.message || '';

    if (typeof code !== 'string') return 'An unexpected error occurred. Please try again.';

    // Auth related errors
    if (code.includes('auth/invalid-credential') || code.includes('auth/user-not-found') || code.includes('auth/wrong-password')) {
        return 'Invalid email or password. Please check your credentials.';
    }
    if (code.includes('auth/email-already-in-use')) {
        return 'This email is already registered. Please login instead.';
    }
    if (code.includes('auth/weak-password')) {
        return 'Password is too weak. Please use at least 6 characters.';
    }
    if (code.includes('auth/too-many-requests')) {
        return 'Too many failed login attempts. Please try again later.';
    }
    if (code.includes('auth/user-disabled')) {
        return 'This account has been disabled. Please contact support.';
    }
    if (code.includes('auth/operation-not-allowed')) {
        return 'This login method is not enabled. Please contact support.';
    }
    if (code.includes('auth/network-request-failed')) {
        return 'Network error. Please check your internet connection.';
    }
    if (code.includes('auth/popup-closed-by-user')) {
        return 'Login cancelled. Please try again.';
    }

    // Firestore related errors
    if (code.includes('permission-denied')) {
        return 'You do not have permission to perform this action.';
    }
    if (code.includes('not-found')) {
        return 'The requested information could not be found.';
    }

    // Default fallback
    return error?.message || 'Something went wrong. Please try again.';
};

/**
 * Handles error with toast notifications
 * @param {Error|Object} error The error object
 * @param {Object} toast The toast instance from vue-toastification
 * @param {string} fallbackMsg Optional fallback message
 */
export const handleError = (error, toast, fallbackMsg = 'An error occurred') => {
    console.error('Handled Error:', error);
    const friendlyMessage = formatFirebaseError(error);
    toast.error(friendlyMessage || fallbackMsg);
};
